import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InstallmentBankTransferReportRow } from '../../../../../api/types/reports/accountant/installment-bank-transfer-report.type';
import { StandingOrderBankTransferCancelReasonDetailsEntity } from '../../../../../api/types/settings/standing-order-bank-transfer/standing-order-bank-transfer.type';
import { refundAndFailBankTransferInstallmentOperation } from '../operations/refund-and-fail-bank-transfer-installment-operation';
import { getStandingOrderBankTransferCancelReasonsListOperation } from '../../../../settings/services/standing-order-bank-transfer-cancel-reason/operations/get-standing-order-bank-transfer-cancel-reasons-list-operation';

const CONFIRM_WORD = 'delete';

// Pull all cancel reasons in one go for the dropdown. The backend has no
// hard max on `limit`; 500 is safely above the realistic count and matches
// the convention used elsewhere in this codebase for "fetch all" calls.
const CANCEL_REASONS_FETCH_LIMIT = 500;

const formatPenalty = (price: number | null | undefined): string => {
  if (typeof price !== 'number') {
    return '—';
  }
  return `₪${Number(price).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const renderCancelReasonOption = (
  option: StandingOrderBankTransferCancelReasonDetailsEntity,
) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      direction: 'rtl',
    }}
  >
    <span>{option.cancelledReason}</span>
    <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
      קנס: {formatPenalty(option.paymentServicePrice)}
    </span>
  </div>
);

interface RefundAndFailBankTransferInstallmentDialogProps {
  visible: boolean;
  row: InstallmentBankTransferReportRow | null;
  onHide: () => void;
  onSuccess: () => void;
}

export const RefundAndFailBankTransferInstallmentDialog: React.FC<
  RefundAndFailBankTransferInstallmentDialogProps
> = ({ visible, row, onHide, onSuccess }) => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  // Cancel reasons list (loaded once per dialog open).
  const [cancelReasons, setCancelReasons] = useState<
    StandingOrderBankTransferCancelReasonDetailsEntity[]
  >([]);
  // Use a no-op setter for `total` since we only need the rows for the dropdown.
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selectedCancelReasonId, setSelectedCancelReasonId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setConfirmText('');
    setSelectedCancelReasonId(null);

    let cancelled = false;
    const fetchReasons = async () => {
      await getStandingOrderBankTransferCancelReasonsListOperation(
        0,
        CANCEL_REASONS_FETCH_LIMIT,
        (data) => {
          if (cancelled) return;
          setCancelReasons(data);
        },
        () => {
          // total not needed for the dropdown
        },
        (l) => {
          if (cancelled) return;
          setReasonsLoading(l);
        },
      );
    };
    fetchReasons();

    return () => {
      cancelled = true;
    };
  }, [visible]);

  const canConfirm =
    confirmText === CONFIRM_WORD &&
    row !== null &&
    selectedCancelReasonId !== null &&
    !loading;

  const handleClose = () => {
    if (loading) return;
    setConfirmText('');
    setSelectedCancelReasonId(null);
    onHide();
  };

  const handleConfirm = async () => {
    if (!row || !canConfirm || selectedCancelReasonId === null) return;

    await refundAndFailBankTransferInstallmentOperation(
      row.uid,
      row.installmentPlanId,
      row.paymentInstallmentsBankTransfer,
      selectedCancelReasonId,
      setLoading,
      () => {
        onSuccess();
        handleClose();
      },
    );
  };

  return (
    <Dialog
      header="החזר וביטול תשלום מוצלח"
      visible={visible && row !== null}
      onHide={handleClose}
      style={{ width: '520px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label="אישור — המשך"
            icon="pi pi-check"
            severity="danger"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
          />
        </div>
      }
    >
      {row ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ margin: 0, lineHeight: 1.6, color: '#374151' }}>
            האם אתה בטוח שברצונך להמשיך? פעולה זו תסמן את התשלום כנכשל לאחר
            ביצוע <strong>החזר כספי</strong> עבור התשלום המוצלח (הוראת קבע
            בהעברה בנקאית).
          </p>

          <div>
            <label
              htmlFor="refundFailCancelReasonDropdown"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#374151',
              }}
            >
              סיבת ביטול:
            </label>
            <Dropdown
              inputId="refundFailCancelReasonDropdown"
              value={selectedCancelReasonId}
              options={cancelReasons}
              optionLabel="cancelledReason"
              optionValue="id"
              onChange={(e) => setSelectedCancelReasonId(e.value)}
              placeholder={
                reasonsLoading
                  ? 'טוען סיבות ביטול...'
                  : cancelReasons.length === 0
                    ? 'לא הוגדרו סיבות ביטול'
                    : 'בחר סיבת ביטול'
              }
              disabled={loading || reasonsLoading || cancelReasons.length === 0}
              filter
              filterBy="cancelledReason"
              emptyMessage="לא הוגדרו סיבות ביטול"
              emptyFilterMessage="לא נמצאו תוצאות"
              style={{ width: '100%' }}
              itemTemplate={renderCancelReasonOption}
              valueTemplate={(_value, props) => {
                const selected = cancelReasons.find(
                  (r) => r.id === selectedCancelReasonId,
                );
                if (!selected) {
                  return (
                    <span style={{ color: '#6b7280' }}>
                      {props.placeholder}
                    </span>
                  );
                }
                return renderCancelReasonOption(selected);
              }}
            />
            {!reasonsLoading && cancelReasons.length === 0 && (
              <div
                style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#991B1B',
                }}
              >
                יש להגדיר סיבות ביטול בהגדרות לפני ביצוע פעולה זו.
              </div>
            )}
          </div>

          <div
            style={{
              padding: '12px 14px',
              backgroundColor: '#FEF2F2',
              borderRadius: '8px',
              border: '1px solid #FECACA',
              fontSize: '14px',
              color: '#991B1B',
              lineHeight: 1.5,
            }}
          >
            <i
              className="pi pi-exclamation-triangle"
              style={{ marginLeft: '8px' }}
            />
            לא ניתן לבטל פעולת החזר לאחר אישור. ודא שזה התשלום הנכון לפני המשך.
          </div>
          <div>
            <label
              htmlFor="refundFailConfirmInput"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#374151',
              }}
            >
              לאישור, הקלד את המילה <strong>{CONFIRM_WORD}</strong>:
            </label>
            <InputText
              id="refundFailConfirmInput"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={loading}
              style={{ width: '100%', direction: 'ltr' }}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
            />
          </div>
        </div>
      ) : null}
    </Dialog>
  );
};
